import React, { useState, useEffect, useCallback } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from 'src/trpc';
import { DataTable } from 'components/table/DataTable';
import { SortingState } from '@tanstack/react-table';
import { Search } from 'components/Search';
import { useSearchParams } from 'react-router-dom';
import { createMaterialColumns } from 'src/components/materials/MaterialColumns';
import { useDebounce } from 'components/Debounce';
import { Loader } from 'components/Loader';
import { Material } from 'src/trpc';
import { MainLayout } from 'src/pages/MainLayout';
import { MaterialSheet } from 'src/components/materials/MaterialSheet';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useOrganization } from 'src/context/OrganizationContext';

const MaterialDashboardPage: React.FC = () => {
  return (
    <MainLayout>
      <MaterialDashboard />
    </MainLayout>
  );
};

const MaterialDashboard: React.FC = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [params, setParams] = useSearchParams({
    offset: '0',
    limit: '25',
    search: '',
    sortDirection: 'desc',
    sortKey: 'createdAt',
    archived: 'all',
  });

  const [search, setSearch] = useState(params.get('search') ?? '');
  const debouncedSearch = useDebounce(search, 500);

  // Drawer state management
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  const offset = parseInt(params.get('offset') ?? '0');
  const limit = parseInt(params.get('limit') ?? '25');
  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  const { data, isLoading, isPending, isFetching } =
    trpc.admin.material.getAllMaterialsWithContainerInfo.useQuery(
      {
        offset,
        limit,
        search: params.get('search') ?? '',
        sortDirection: (params.get('sortDirection') ?? 'desc') as 'asc' | 'desc',
        sortKey: params.get('sortKey')?.trim() || 'createdAt',
        archived: (params.get('archived') ?? 'all') as 'active' | 'archived' | 'all',
      },
      {
        placeholderData: keepPreviousData,
        enabled: isResolved && !!organizationId,
      },
    );

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, isFetching, setIsOrganizationSwitching]);

  const updateSearchParams = useCallback(
    (searchValue: string) => {
      const newParams = new URLSearchParams(params);
      newParams.set('search', searchValue);
      newParams.set('offset', '0');
      setParams(newParams);
    },
    [params, setParams],
  );

  useEffect(() => {
    const currentSearch = params.get('search') ?? '';
    if (debouncedSearch !== currentSearch) {
      updateSearchParams(debouncedSearch);
    }
  }, [debouncedSearch, params, updateSearchParams]);

  useEffect(() => {
    if (sorting.length) {
      const newParams = new URLSearchParams(params);
      newParams.set('sortDirection', sorting[0].desc ? 'desc' : 'asc');
      newParams.set('sortKey', sorting[0].id || 'createdAt');
      newParams.set('offset', '0');
      setParams(newParams);
    }
  }, [sorting, params, setParams]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleArchivedFilter = (value: string) => {
    const newParams = new URLSearchParams(params);
    newParams.set('archived', value);
    newParams.set('offset', '0'); // Reset to first page
    setParams(newParams);
  };

  const handleEditMaterial = useCallback((material: Material) => {
    setSelectedMaterial(material);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedMaterial(null);
  };

  const displayData = data?.materials || [];
  const total = data?.total || 0;

  if (!isResolved || isOrganizationSwitching || (isPending && isFetching && !data)) {
    return <Loader title="Loading materials" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-semibold">Materials</h2>
        <MaterialSheet
          trigger={
            <Button variant="default" className="text-white">
              Create New Material
            </Button>
          }
        />
      </div>

      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <Search
            value={search}
            onChange={handleSearch}
            placeholder="Search materials by name"
            isLoading={isLoading}
          />
        </div>
        <div className="w-48">
          <Select value={params.get('archived') ?? 'all'} onValueChange={handleArchivedFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Materials</SelectItem>
              <SelectItem value="active">Active Only</SelectItem>
              <SelectItem value="archived">Archived Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col h-full">
        <DataTable
          columns={createMaterialColumns(handleEditMaterial)}
          data={displayData}
          currentPage={currentPage}
          sorting={sorting}
          onSortingChange={setSorting}
          hasNextPage={total > offset + limit}
          hasPreviousPage={offset > 0}
          onPageChange={(change) => {
            const newOffset = Math.max(0, offset + change * limit);
            setParams((prev) => {
              const newParams = new URLSearchParams(prev);
              newParams.set('offset', newOffset.toString());
              return newParams;
            });
          }}
          pageCount={Math.ceil(total / limit)}
          onRowClick={handleEditMaterial}
          total={total}
          resourceName="materials"
          offset={offset}
          limit={limit}
        />
      </div>

      {/* Material Sheet */}
      <MaterialSheet
        material={selectedMaterial}
        isOpen={isDrawerOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseDrawer();
          }
        }}
      />
    </div>
  );
};

export { MaterialDashboardPage };
export default MaterialDashboard;
